import {
  CHANGE_FILTER_GROUPS,
  ChangeFilterGroup,
  ClientAdministrationChange,
  DELETE_REASONS,
  DeleteReason,
} from "@/data/models/ClientAdministrationRow";
import { EMPTY_VALUE, targetAppBuildText } from "@/utils/ClientAdministrationDetail";
import { recalculateDryingsStatusText } from "@/utils/ClientHelperFunctions";
import { PRODUCT_CHOICES, productChoiceLabelKey } from "@/utils/ClientProducts";
import { deleteReasonLabel } from "@/utils/ClientStatus";
import { liveOperationValue } from "@/utils/ClientLiveOperation";
import { formatDateToDayMonthYear, formatDateToHoursAndMinuteString } from "@/utils/DateHelper";
import {
  DEVICE_UPDATE_LABEL_KEYS,
  FUNCTION_SWITCH_LABEL_KEYS,
  LICENCE_LABEL_KEYS,
  iotUpdateConditionText,
  projectOrganisationText,
} from "@/utils/ClientSetupSummary";
import { IOT_UPDATE_CONDITIONS, IotUpdateCondition, ProjectOrganisationApp } from "@/data/models/ClientSetup";
import { PARTNER_ACCEPTS_FIELDS, PARTNER_ENDPOINT_FIELDS, PARTNER_STATUSES, PartnerStatus } from "@/data/models/ClientPartner";
import { ClientSystemConfigurationValue } from "@/data/models/ClientAdministrationRow";
import { SYSTEM_CONFIGURATION_FIELD_LABEL_KEYS, systemConfigurationValueText } from "@/utils/ClientSystemConfiguration";

/**
 * The Verlauf of a client in the IRES Admin Client: each change entry as the card shows it. Pure; the
 * caller passes the translator. The rows' shape: IRES Cloud `docs/apis/list-clients-for-administration.md`.
 */

type Translate = (key: string, values?: unknown[]) => string;

/** The groups the Verlauf leaves out: every filter group not ticked (#2610071). */
export function excludedChangeGroups(shown: ChangeFilterGroup[]): ChangeFilterGroup[] {
  return CHANGE_FILTER_GROUPS.filter((group) => !shown.includes(group));
}

const DETAIL = "admin.clients.detail";

const PARTNERS = "admin.clients.partners";

/** A partner's recorded fields (`partner.<field>`, `partner.credentials.<field>`) and their labels (#2610084). */
const PARTNER_FIELD_LABEL_KEYS: Record<string, string> = {
  "partner.integrationStatus": `${PARTNERS}.fields.integrationStatus`,
  "partner.baseUrl": `${PARTNERS}.fields.baseUrl`,
  // A creation and a new password (merge 3).
  "partner.email": `${PARTNERS}.access.email`,
  "partner.system": `${PARTNERS}.fields.system`,
  "partner.signIn": `${PARTNERS}.fields.signIn`,
  "partner.password": `${PARTNERS}.access.password`,
  "partner.linkedClient": `${PARTNERS}.links.linkedClient`,
  // The partner's contact people (merge 4).
  "partner.contacts": `${PARTNERS}.contacts.title`,
  // An integration package (#2610090).
  "partner.package.version": `${PARTNERS}.packages.apiVersion`,
  "partner.package.groups": `${PARTNERS}.packages.groups`,
  "partner.package.recipient": `${PARTNERS}.packages.recipient`,
};
PARTNER_ENDPOINT_FIELDS.forEach((field) => {
  PARTNER_FIELD_LABEL_KEYS[`partner.endpoints.${field}`] = `${PARTNERS}.endpoints.${field}`;
});
PARTNER_ACCEPTS_FIELDS.forEach((field) => {
  PARTNER_FIELD_LABEL_KEYS[`partner.${field}`] = `${PARTNERS}.accepts.${field}`;
});
["appointmentFieldSwap", "incomingContactPerson0", "sendsLatestProjectStatus", "isTestSystem"].forEach((field) => {
  PARTNER_FIELD_LABEL_KEYS[`partner.${field}`] = `${PARTNERS}.behaviour.${field}`;
});
["apiKey", "password", "email", "clientId", "authUrl", "tokenUrl"].forEach((field) => {
  PARTNER_FIELD_LABEL_KEYS[`partner.credentials.${field}`] = `${PARTNERS}.credentials.${field}`;
});

/** Each recorded field's label, by the labels the detail and its dialogs already use. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  clientName: "admin.clients.masterData.clientName",
  clientNumber: `${DETAIL}.fields.clientNumber`,
  street: `${DETAIL}.fields.street`,
  houseNumber: "admin.clients.masterData.houseNumber",
  zip: `${DETAIL}.fields.zip`,
  city: `${DETAIL}.fields.city`,
  clientType: `${DETAIL}.fields.clientType`,
  iresProjectNumberPrefix: `${DETAIL}.fields.clientShorthand`,
  contractTypes: `${DETAIL}.fields.contracts`,
  hasServiceContractForFirmwaresAndConfigs: `${DETAIL}.fields.serviceContract`,
  hasServiceContractForFirmwaresAndConfigsSince: "admin.clients.changes.fields.serviceContractSince",
  hasWarrantyExtensionForPanels: `${DETAIL}.fields.warrantyExtensionForPanels`,
  hasWarrantyExtensionForPanelsSince: "admin.clients.changes.fields.warrantyExtensionSince",
  liveOperation: `${DETAIL}.fields.liveOperation`,
  miraAllClients: `${DETAIL}.fields.miraAllClients`,
  targetRoomInfoConfigVersion: `${DETAIL}.targetVersions.targetRoomInfoConfigVersion`,
  blocked: `${DETAIL}.fields.blocked`,
  isActive: `${DETAIL}.fields.isActive`,
  deleted: `${DETAIL}.fields.deleted`,
  deleteReason: "admin.clients.status.deleteReason",
  statusNote: "admin.clients.status.noteRequired",
  isTestSystem: `${DETAIL}.fields.isTestSystem`,
  testAppVersion: `${DETAIL}.fields.testAppVersion`,
  testRoomInfoConfigVersion: `${DETAIL}.fields.testRoomInfoConfigVersion`,
  testUsers: `${DETAIL}.fields.testUsers`,
  // The Gruppe card (#2610074).
  isMaster: `${DETAIL}.fields.isMaster`,
  membersAdded: "admin.clients.changes.fields.membersAdded",
  membersRemoved: "admin.clients.changes.fields.membersRemoved",
  settingsSource: `${DETAIL}.fields.settingsSource`,
  masterClient: `${DETAIL}.fields.masterClient`,
  // The Hilfsfunktionen card (#2610077).
  recalculateAllDryings: "admin.clients.helperFunctions.recalculateAllDryings.button",
  // The Produkte card (#2610089).
  officeDeviceAssignment: `${DETAIL}.fields.officeDeviceAssignment`,
  project: `${DETAIL}.fields.project`,
  work: `${DETAIL}.fields.work`,
  interface: `${DETAIL}.fields.interface`,
  projectManagement: `${DETAIL}.fields.productChoice`,
  // The Projekttypen card (#2610081): IRES's list, and the types on in the client's own table.
  allowedProjectTypeIds: "admin.clients.changes.fields.allowedProjectTypeIds",
  activeProjectTypeIds: "admin.clients.changes.fields.activeProjectTypeIds",
  // The guided setup (#2610082): how the client was created.
  group: `${DETAIL}.sections.group`,
  newProjectsIn: "settings.projectManagement.projectSettings.newProjectsIn",
  editProjectsIn: "settings.projectManagement.projectSettings.editProjectsIn",
  newAppointmentsIn: "settings.projectManagement.projectSettings.newAppointmentsIn",
  finaliseProjectsIn: "settings.projectManagement.projectSettings.finaliseProjectsIn",
  enabledFunctions: "admin.clients.changes.fields.enabledFunctions",
  firstUser: "admin.clients.setup.users.firstUser",
  // Bereinigen on Geteilte Benutzer (2026-11-03): the address, who was taken out, the entry deleted.
  sharedEmailAddress: "admin.clients.changes.fields.sharedEmailAddress",
  sharedEmailAddressUsersRemoved: "admin.clients.changes.fields.sharedEmailAddressUsersRemoved",
  sharedEmailAddressDeleted: "admin.clients.changes.fields.sharedEmailAddressDeleted",
  // Merge 2: the licence counts ("licences.basic") and the IoT update conditions.
  ...Object.fromEntries(Object.entries(LICENCE_LABEL_KEYS).map(([key, labelKey]) => [`licences.${key}`, labelKey])),
  ...Object.fromEntries(DEVICE_UPDATE_LABEL_KEYS.map((entry) => [entry.field, entry.labelKey])),
  // A partner's page (#2610084): the partner's name first, then each field of the card.
  partner: "admin.clients.partners.partner",
  ...PARTNER_FIELD_LABEL_KEYS,
  // The Systemkonfiguration card and IRES Office's own page (#2610141), and Mira at creation.
  ...SYSTEM_CONFIGURATION_FIELD_LABEL_KEYS,
};

const DEVICE_UPDATE_FIELDS = DEVICE_UPDATE_LABEL_KEYS.map((entry) => entry.field as string);

/** The Systemkonfiguration switches a creation records as on, by their labels (#2610082). */
const FUNCTION_LABEL_KEYS: Record<string, string> = {
  ...Object.fromEntries(FUNCTION_SWITCH_LABEL_KEYS.map((entry) => [entry.field, entry.labelKey])),
  enablePersonalChat: "admin.clients.setup.functions.personalChat",
  enableProjectChat: "admin.clients.setup.functions.projectChat",
  enableAiPredictions: "settings.aiPredictions.title",
};

/** The Projektorganisation questions a creation and the Projektorganisation card record. */
const PROJECT_ORGANISATION_FIELDS = ["newProjectsIn", "editProjectsIn", "newAppointmentsIn", "finaliseProjectsIn"];

/** Fields recorded with no "before": shown as their value alone ("Grund: Duplikat"). */
const SINGLE_VALUE_FIELDS = [
  "deleteReason",
  "statusNote",
  "membersAdded",
  "membersRemoved",
  "recalculateAllDryings",
  "partner",
  "sharedEmailAddress",
  "sharedEmailAddressUsersRemoved",
  "sharedEmailAddressDeleted",
];

/** Each group's name: the card or action it came from. */
const GROUP_LABEL_KEYS: Record<string, string> = {
  MASTER_DATA: `${DETAIL}.sections.masterData`,
  CONTRACTS: `${DETAIL}.sections.contracts`,
  LIVE_OPERATION: `${DETAIL}.fields.liveOperation`,
  MIRA_ALL_CLIENTS: `${DETAIL}.fields.miraAllClients`,
  INFO_CONFIG_TARGET: "admin.clients.changes.groups.INFO_CONFIG_TARGET",
  CLIENT_LOGIN: "admin.clients.changes.groups.CLIENT_LOGIN",
  BLOCKED: `${DETAIL}.fields.blocked`,
  ACTIVE: `${DETAIL}.fields.isActive`,
  DELETED: `${DETAIL}.fields.deleted`,
  TEST_SETTINGS: `${DETAIL}.sections.testSettings`,
  GROUP: `${DETAIL}.sections.group`,
  HELPER_FUNCTIONS: `${DETAIL}.sections.helperFunctions`,
  PRODUCTS: `${DETAIL}.sections.products`,
  PROJECT_TYPES: `${DETAIL}.sections.projectTypes`,
  PROJECT_ORGANISATION: `${DETAIL}.sections.projectOrganisation`,
  SYSTEM_CONFIGURATION: `${DETAIL}.sections.systemConfiguration`,
  CREATED: "admin.clients.changes.groups.CREATED",
  PARTNER_STATUS: `${PARTNERS}.cards.status`,
  PARTNER_CONNECTION: `${PARTNERS}.cards.connection`,
  PARTNER_ACCEPTS: `${PARTNERS}.changeGroups.accepts`,
  PARTNER_BEHAVIOUR: `${PARTNERS}.cards.behaviour`,
  PARTNER_CREDENTIALS: `${PARTNERS}.changeGroups.credentials`,
  PARTNER_CREATED: `${PARTNERS}.changeGroups.created`,
  PARTNER_PASSWORD: `${PARTNERS}.changeGroups.password`,
  PARTNER_LINK: `${PARTNERS}.links.title`,
  PARTNER_CONTACTS: `${PARTNERS}.contacts.title`,
  PARTNER_PACKAGE: `${PARTNERS}.packages.changeGroup`,
  SHARED_EMAIL_ADDRESS: "admin.clients.changes.groups.SHARED_EMAIL_ADDRESS",
};

/** One changed field as the card shows it. */
export interface ChangeLine {
  label: string;
  before: string;
  after: string;
  /** Shown as "label: after", with no before (a delete reason, a note). */
  single?: boolean;
}

/** One entry as the card shows it: "14.10.2026 11:32", who, which card, and its lines. */
export interface ChangeEntryView {
  id: string;
  when: string;
  who: string;
  group: string;
  lines: ChangeLine[];
}

/** A field's value, readable: by the field's own labels where it has them, "–" for nothing. */
export function changeValueText(field: string, value: unknown, t: Translate): string {
  if (field === "targetRoomInfoConfigVersion" && (value === null || value === undefined)) {
    return t("admin.clients.changes.followsGlobalTarget");
  }
  // No list stored: the client got every type of its products (#2610081).
  if (field === "allowedProjectTypeIds" && (value === null || value === undefined)) {
    return t("admin.clients.changes.everyProjectType");
  }
  // Project type ids (#2610081) by their codes, "LO, TR".
  if ((field === "allowedProjectTypeIds" || field === "activeProjectTypeIds") && Array.isArray(value)) {
    return value.length === 0 ? EMPTY_VALUE : value.map((id) => t(`projectDetails.projectTypeCodes.${id}`)).join(", ");
  }
  // The guided setup (#2610082): the group, the Projektorganisation answers, the functions switched on.
  if (field === "group" && typeof value === "string" && ["STANDALONE", "MEMBER", "MASTER"].includes(value)) {
    return t(`admin.clients.setup.group.kinds.${value}`);
  }
  if (PROJECT_ORGANISATION_FIELDS.includes(field) && Array.isArray(value)) {
    return projectOrganisationText(value as ProjectOrganisationApp[], t);
  }
  // A partner's page (#2610084): its status by name, a credential only as replaced, the unset contact rule.
  if (field === "partner.integrationStatus" && typeof value === "string" && PARTNER_STATUSES.includes(value as PartnerStatus)) {
    return t(`${PARTNERS}.statuses.${value}`);
  }
  if (field === "partner.system" && typeof value === "string") {
    return t(value === "EASY_TEC" || value === "EXTERNAL" ? `${PARTNERS}.systems.${value}` : `${PARTNERS}.systems.NONE`);
  }
  if (field === "partner.signIn" && typeof value === "string" && ["API_KEY", "SIGN_IN", "EASY_TEC"].includes(value)) {
    return t(`${PARTNERS}.signIn.${value}`);
  }
  if (field.startsWith("partner.credentials.") || field === "partner.password") {
    return value === "REPLACED" ? t(`${PARTNERS}.credentials.replaced`) : EMPTY_VALUE;
  }
  // A package's endpoint groups (#2610090) by the names its dialog gives them.
  if (field === "partner.package.groups" && Array.isArray(value)) {
    return value.length === 0 ? EMPTY_VALUE : value.map((group) => t(`${PARTNERS}.packages.groupNames.${group}`)).join(", ");
  }
  // The contact list (merge 4) by name and role: "Max Muster (Technik), Eva Schmidt (Fachlich)".
  if (field === "partner.contacts" && Array.isArray(value)) {
    return value.length === 0
      ? EMPTY_VALUE
      : value
        .map((contact) => {
          const { name, role } = (contact ?? {}) as { name?: unknown; role?: unknown };
          const roleText = role === "TECHNICAL" || role === "BUSINESS" ? t(`${PARTNERS}.contacts.roles.${role}`) : null;
          return roleText ? `${String(name ?? "")} (${roleText})` : String(name ?? "");
        })
        .join(", ");
  }
  if (field === "partner.incomingContactPerson0" && (value === null || value === undefined)) {
    return t(`${PARTNERS}.byRoute`);
  }
  if (DEVICE_UPDATE_FIELDS.includes(field) && IOT_UPDATE_CONDITIONS.includes(value as IotUpdateCondition)) {
    return iotUpdateConditionText(value as IotUpdateCondition, t);
  }
  if (field === "enabledFunctions" && Array.isArray(value)) {
    return value.length === 0 ? EMPTY_VALUE : value.map((flag) => (FUNCTION_LABEL_KEYS[flag] ? t(FUNCTION_LABEL_KEYS[flag]) : String(flag))).join(", ");
  }
  // The Systemkonfiguration (#2610141): ASSIST's scope and Mira by name, the switches as Ja / Nein.
  if (field in SYSTEM_CONFIGURATION_FIELD_LABEL_KEYS) {
    const text = systemConfigurationValueText(field as keyof ClientSystemConfigurationValue, value, t);
    if (text !== null) {
      return text;
    }
  }
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }
  if (field.endsWith("Since") && typeof value === "number") {
    return formatDateToDayMonthYear(new Date(value)) ?? EMPTY_VALUE;
  }
  if (typeof value === "boolean") {
    return t(value ? "common.yes" : "common.no");
  }
  // The test users (#2610070), the members added or removed (#2610074) and the users taken out of a
  // shared email address (2026-11-03) are recorded by name.
  if (
    (field === "testUsers" || field === "membersAdded" || field === "membersRemoved" || field === "sharedEmailAddressUsersRemoved") &&
    Array.isArray(value)
  ) {
    return value.length === 0 ? EMPTY_VALUE : value.map(String).join(", ");
  }
  if (field === "testAppVersion" && typeof value === "string") {
    return targetAppBuildText(value);
  }
  // A recalculation run's status (#2610077): Gestartet / Läuft bereits / Nichts zu tun.
  if (field === "recalculateAllDryings" && typeof value === "string") {
    return recalculateDryingsStatusText(value, t);
  }
  // The derived product choice (#2610089): shown with the product-choice labels, else as stored.
  if (field === "projectManagement" && typeof value === "string") {
    const key = PRODUCT_CHOICES.includes(value as never) ? productChoiceLabelKey(value) : null;
    return key ? t(key) : String(value);
  }
  if (field === "contractTypes" && Array.isArray(value)) {
    return value.length === 0 ? EMPTY_VALUE : value.map((type) => t(`${DETAIL}.contractTypes.${type}`)).join(", ");
  }
  if (field === "clientType" && typeof value === "string" && ["IRES", "CLIENT", "OTHER"].includes(value)) {
    return t(`${DETAIL}.clientTypes.${value}`);
  }
  if (field === "deleteReason" && typeof value === "string" && DELETE_REASONS.includes(value as DeleteReason)) {
    return deleteReasonLabel(value as DeleteReason, t);
  }
  if (field === "liveOperation" && typeof value === "object") {
    const marker = value as { live?: unknown; reason?: unknown };
    return liveOperationValue(
      { live: marker.live !== false, reason: (marker.reason as never) ?? null, changedAtMillis: null, changedByName: null },
      t
    );
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/**
 * One Verlauf entry for the card. An unknown group or field is shown by its stored name rather than
 * dropped, so a newer IRES Cloud never hides a change from an older IRES Office.
 */
export function toChangeEntryView(entry: ClientAdministrationChange, t: Translate): ChangeEntryView {
  const date = entry.atMillis !== null ? new Date(entry.atMillis) : null;
  const groupKey = entry.group !== null ? GROUP_LABEL_KEYS[entry.group] : undefined;

  return {
    id: entry.id,
    when: date ? `${formatDateToDayMonthYear(date)} ${formatDateToHoursAndMinuteString(date)}` : EMPTY_VALUE,
    who: entry.byName ?? EMPTY_VALUE,
    group: groupKey ? t(groupKey) : entry.group ?? EMPTY_VALUE,
    lines: entry.changes.map((change) => ({
      label: FIELD_LABEL_KEYS[change.field] ? t(FIELD_LABEL_KEYS[change.field]) : change.field,
      before: changeValueText(change.field, change.before, t),
      after: changeValueText(change.field, change.after, t),
      // A creation has no "before": every value stands alone.
      // A replaced credential has no value to show either side (#2610084).
      ...(SINGLE_VALUE_FIELDS.includes(change.field) ||
        change.field.startsWith("partner.credentials.") ||
        change.field === "partner.password" ||
        entry.group === "CREATED" ||
        entry.group === "PARTNER_PACKAGE" ||
        entry.group === "PARTNER_CREATED"
        ? { single: true }
        : {}),
    })),
  };
}
